import moment from 'moment-timezone';
import dayjs from 'dayjs';
import { addNotification, notificationStates } from '@/components/Notification';
import firebase from '@config/firebase.config';
const database = firebase.firestore();

const startOfLocalDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Calendar days on or before today are selectable (regularization is not for future dates). */
export const isRegularizationDateSelectableInCalendar = (date) => {
  const picked = startOfLocalDay(date);
  const today = startOfLocalDay(new Date());
  return picked <= today;
};

export const canUseRegularizationDateSelection = (date) => {
  if (date == null) return true;
  if (isRegularizationDateSelectableInCalendar(date)) return true;
  addNotification({
    message: 'Cannot select future dates. Choose today or an earlier date.',
    level: notificationStates.error,
  });
  return false;
};

/** When the calendar date changes, keep TimePicker dayjs values on that same day (H:M preserved). */
export const buildRegularizationFormDateChange = (prev, selectedDate) => {
  const mid = new Date(selectedDate);
  mid.setHours(0, 0, 0, 0);
  const base = dayjs(mid).startOf('day');
  return {
    ...prev,
    date: mid,
    startTime: prev.startTime
      ? base
          .hour(prev.startTime.hour())
          .minute(prev.startTime.minute())
          .second(0)
      : prev.startTime,
    endTime: prev.endTime
      ? base.hour(prev.endTime.hour()).minute(prev.endTime.minute()).second(0)
      : prev.endTime,
  };
};
export const getProjectDropdownOptions = (teams) => {
  const dropdownOptions = [];
  if (Array.isArray(teams?.projects)) {
    teams?.projects?.forEach((project) => {
      if (!project.data.deleted) {
        dropdownOptions.push({
          ...project,
          value: project.id,
          label: project?.data?.project_name,
        });
      }
    });
  }
  return dropdownOptions;
};
export const getTaskDropdownOptions = (taskLists) => {
  const dropdownOptions = [];
  taskLists.forEach((task) => {
    dropdownOptions.push({
      ...task,
      value: task.id,
      label: task.Title,
    });
  });
  return dropdownOptions;
};

export const isManagerOrAdminRole = (m) => {
  const role = String(m?.userType || m?.role || '')
    .trim()
    .toLowerCase();
  return role === 'admin' || role === 'manager';
};

/** Managers/Admins for the assignee dropdown from a team roster map (userUID → member row). */
export const managersFromMemberDetails = (memberDetails) => {
  if (!memberDetails || typeof memberDetails !== 'object') return [];
  return Object.entries(memberDetails)
    .filter(([, m]) => isManagerOrAdminRole(m))
    .map(([uid, m]) => ({
      userUID: uid,
      userName: m?.userName || m?.userEmailid || uid,
    }))
    .sort((a, b) => (a.userName || '').localeCompare(b.userName || ''));
};

export const getManagerDropdownOptions = (availableManagers) => {
  const dropdownOptions = [];
  availableManagers.forEach((manager) => {
    dropdownOptions.push({
      ...manager,
      value: manager.userUID,
      label: manager.userName,
    });
  });
  return dropdownOptions;
};

/** Reporter column: prefer name stored on the request at create/update time. */
export const getRegularizationReporterLabel = (row, memberDetails) => {
  const stored = row?.userName != null ? String(row.userName).trim() : '';
  if (stored) return stored;
  const m = memberDetails?.[row?.userId];
  return m?.userName || m?.userEmailid || row?.userId || '';
};

/** Options for team dropdown from `useUserTeamsForCompany` rows. */
export const getTeamDropdownOptions = (teamsList) => {
  if (!Array.isArray(teamsList)) return [];
  return teamsList.map((t) => ({
    ...t,
    value: t.teamId,
    label: t.teamName,
  }));
};

export const calculateDuration = (startTime, endTime) => {
  // Check if both times are selected
  if (!startTime || !endTime) {
    return { hours: 0, minutes: 0, isValid: false };
  }

  try {
    // TimePicker from antd returns moment objects
    // Extract hours and minutes directly
    const startHour = startTime.hour ? startTime.hour() : 0;
    const startMinute = startTime.minute ? startTime.minute() : 0;
    const endHour = endTime.hour ? endTime.hour() : 0;
    const endMinute = endTime.minute ? endTime.minute() : 0;

    // Convert to total minutes for easier calculation
    const startTotalMinutes = startHour * 60 + startMinute;
    let endTotalMinutes = endHour * 60 + endMinute;

    // Handle case where end time is before start time (next day scenario)
    if (endTotalMinutes <= startTotalMinutes) {
      endTotalMinutes += 24 * 60; // Add 24 hours
    }

    const diffMinutes = endTotalMinutes - startTotalMinutes;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return {
      hours,
      minutes,
      isValid: diffMinutes > 0 && diffMinutes <= 24 * 60,
    };
  } catch (error) {
    console.error('Error calculating duration:', error);
    return { hours: 0, minutes: 0, isValid: false };
  }
};
export const getDefaultTimezone = () => {
  return typeof moment.tz !== 'undefined' && moment.tz.guess
    ? moment.tz.guess()
    : 'Asia/Kolkata';
};

export const validateForm = (formData) => {
  if (!formData.date) {
    addNotification({
      message: 'Please select a date',
      level: notificationStates.error,
    });
    return false;
  }

  const selectedDate = moment(formData.date);
  const currentDate = moment();

  if (selectedDate.isAfter(currentDate, 'day')) {
    addNotification({
      message: 'Cannot add regularization for future date',
      level: notificationStates.error,
    });
    return false;
  }

  if (selectedDate.year() < 2023) {
    addNotification({
      message: 'Please select a date after 2023',
      level: notificationStates.error,
    });
    return false;
  }

  if (!formData.team?.value) {
    addNotification({
      message: 'Please select a team',
      level: notificationStates.error,
    });
    return false;
  }

  if (!formData.assignee || formData.assignee === 'select a manager') {
    addNotification({
      message: 'Please select a manager',
      level: notificationStates.error,
    });
    return false;
  }

  if (!formData.startTime || !formData.endTime) {
    addNotification({
      message: 'Please select both start and end times',
      level: notificationStates.error,
    });
    return false;
  }
  const duration = calculateDuration(formData.startTime, formData.endTime);
  if (!duration.isValid) {
    if (duration.hours === 0 && duration.minutes === 0) {
      addNotification({
        message: 'Start and end times cannot be the same',
        level: notificationStates.error,
      });
    } else {
      addNotification({
        message: 'Duration cannot exceed 24 hours',
        level: notificationStates.error,
      });
    }
    return false;
  }

  return true;
};
export const check24HoursLimit = async (formData, teams, profile) => {
  const teamId =
    formData.team?.value ??
    teams.active?.activeTeamid ??
    teams.active?.activeTeamId;
  if (!formData.date || !teamId) return true;

  try {
    const dateStr = moment(formData.date).format('DD:MM:YYYY');
    const dateId = dateStr.replace(/(^|-|:)0+/g, '$1');
    const userId = profile.id;

    const snapshot = await database
      .collection('memberData')
      .doc(teamId)
      .collection('daysData')
      .doc(userId)
      .collection('dateId')
      .doc(dateId)
      .get();

    const existingData = snapshot.data();
    const existingMinutes = snapshot.exists
      ? (existingData.totalHours || 0) * 60 + (existingData.totalMinutes || 0)
      : 0;

    const duration = calculateDuration(formData.startTime, formData.endTime);
    const newMinutes = duration.hours * 60 + duration.minutes;
    const totalMinutes = existingMinutes + newMinutes;

    const isToday = moment(formData.date).isSame(moment(), 'day');
    if (isToday) {
      const currentMinutes = moment().hours() * 60 + moment().minutes();
      if (totalMinutes > currentMinutes) {
        addNotification({
          message: 'Logged time exceeds current time',
          level: notificationStates.error,
        });
        return false;
      }
    } else {
      if (totalMinutes > 24 * 60) {
        addNotification({
          message: 'Total time exceeds 24 hours for this date',
          level: notificationStates.error,
        });
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking 24 hours limit:', error);
    return true;
  }
};

export const getInitialFormData = (defaultTimezone) => {
  return {
    team: null,
    projectId: null,
    projectName: '',
    taskId: null,
    taskName: '',
    taskDetails: '',
    assignee: '',
    date: null,
    startTime: null,
    endTime: null,
    timezone: defaultTimezone,
  };
};

export const getHoursMinutes = (value) => {
  if (!value) return { hours: 0, minutes: 0 };
  if (typeof value.hour === 'function' && typeof value.minute === 'function') {
    return { hours: value.hour(), minutes: value.minute() };
  }
  const m = moment.isMoment(value) ? value : moment(value);
  return { hours: m.hour(), minutes: m.minute() };
};
